import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import TopNav from './TopNav';
import Footer from './Footer';
import { useI18n } from '@/i18n/I18nProvider';
import { legal } from '@/i18n/legal';

/**
 * Shared shell for static legal / info pages (Privacy, Contact).
 * Provides: top navigation, eyebrow, title, lead, generous typography, and footer.
 */
export default function LegalPageShell({
  eyebrow,
  title,
  lead,
  children,
  testId,
  titleTestId,
  backTestId,
}) {
  const { locale } = useI18n();
  const backLabel = legal[locale]?.contact?.backHome || 'Back to home';

  return (
    <div data-testid={testId} className="min-h-screen bg-background">
      <TopNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            to="/"
            data-testid={backTestId}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {backLabel}
          </Link>

          {eyebrow && (
            <div className="mt-8 text-xs uppercase tracking-[0.22em] text-primary">
              {eyebrow}
            </div>
          )}
          <h1
            data-testid={titleTestId}
            className="font-display mt-2 text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.05]"
          >
            {title}
          </h1>
          {lead && (
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {lead}
            </p>
          )}

          <div className="mt-10 sm:mt-14">{children}</div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
